/* eslint-disable no-unused-expressions */
/* eslint no-undef: "off"*/
// eslint-disable-next-line
import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router';
import { withRouter } from '../../utils/withRouter';
import { ToastContainer } from 'react-toastify';
import { reportPageview, LANGUAGE } from '../../utils/analytics';
import {setCookie,getCookie} from '../../utils/setCookie';
import { setPwaStatistics } from '../../utils/chekCover';
import LoaderSVG from '../../assets/images/loader_animate.gif';
import { getLink } from '../../utils/chekCover';
import RequireAuth from '../../components/ErrorModal'
import PrivateRoute from './PrivateRoute'
import RestrictedRoute from './RestrictedRoute'
import { lazyWithPreload } from '../../utils/lazyLoading';
import { useNavigationPending } from '../../utils/AppRouter';
import safeStorage, { safeSessionStorage } from '../../utils/safeStorage';
import { isLoggedIn, enforceSessionPolicy } from '../../utils/session';

const Login = lazyWithPreload(() => import('../../components/Login'));
const Home = lazyWithPreload(() => import('../../components/Home/index'));
const Rewards = lazyWithPreload(() => import('../../components/Rewards'));
const SurveyHistory = lazyWithPreload(() => import('../../components/SurveyHistory'));

const Referral = lazyWithPreload(() => import("../../components/ReferralProgram"));
const Help = lazyWithPreload(() => import("../../components/Help"));
const Profile = lazyWithPreload(() => import("../../components/userProfile"));
const Policy = lazyWithPreload(() => import("../../components/Policy"));
const TermnCondition = lazyWithPreload(() => import("../../components/TermsandCondition"));
const Finished = lazyWithPreload(() => import("../../components/Finished"));
const Activation = lazyWithPreload(() => import("../../components/ActivationAccount"));

const ProfileUnsubscribe = lazyWithPreload(() => import("../../components/UnsubscribeAcoount"));
const GdprInfo = lazyWithPreload(() => import("../../components/GDPR"));
const NotFound = lazyWithPreload(() => import("../../components/404-notFound"));
const Affiliate = lazyWithPreload(() => import("../../components/Affiliate"));

// Module scope on purpose: built inside the component this handed React a new
// component type on every render, so every guarded page was unmounted, remounted
// and re-suspended on every render of App.
const HomeGuarded = RequireAuth(Home);
const AffiliateGuarded = RequireAuth(Affiliate);
const RewardsGuarded = RequireAuth(Rewards);
const SurveyHistoryGuarded = RequireAuth(SurveyHistory);
const ReferralGuarded = RequireAuth(Referral);
const HelpGuarded = RequireAuth(Help);
const ProfileGuarded = RequireAuth(Profile);
const PolicyGuarded = RequireAuth(Policy);
const TermnConditionGuarded = RequireAuth(TermnCondition);
const GdprInfoGuarded = RequireAuth(GdprInfo);

// Derived from the route table below, not hand-picked: every other route starts with /:id/, so
// there the first segment IS the language. These are the only literal first segments in the table
// (plus root ""), and none of them is a language code, so a new language-less route is one entry
// here rather than another clause in the condition.
export const LANGUAGE_LESS_SEGMENTS = ["login", "finished", "activate", "unsubscribe"];

let Panel_language = getCookie('panel_langauge')

if(Panel_language === null || Panel_language === undefined || Panel_language === "") {
  Panel_language ="en"
}
if(window.location.pathname.split("/")[1] ==="login") {
  Panel_language ="en"
}

// Credential links survive the login screen in this tab only. The page consumes its entry.
// Do not leave a second copy in end-page-url, which is still persistent for survey callbacks.
// Match the router's decoded, case-insensitive action names without decoding opaque credentials.
const credentialSegments = window.location.pathname.split("/");
[1, 2].forEach(index => {
  try {
    const segment = decodeURIComponent(credentialSegments[index] || "");
    if (/^(activate|unsubscribe)$/i.test(segment)) credentialSegments[index] = segment.toLowerCase();
  } catch {
    return;
  }
});
const credentialLocation = credentialSegments.join("/");
const credentialPath = /^\/(?:[^/]+\/)?(activate|unsubscribe)(?:\/|$)/.test(credentialLocation);
const credentialPage = credentialLocation.match(/^\/(?:[^/]+\/)?(activate|unsubscribe)(?:\/[^/]+\/[^/]+\/[^/]+)\/?$/);
// Runs while the module is imported, before React and the ErrorBoundary exist: blocked storage must
// not throw here (CO-BOOTSTRAP-RAW-STORAGE). Without storage the credential page finds no entry and
// shows its own missing-link state.
safeStorage.setItem("end-page-url", credentialPath ? "/" : window.location.pathname);
safeStorage.removeItem("active-user-url");
safeStorage.removeItem("unsubscribe-user-url");
if (credentialPath) {
  safeSessionStorage.removeItem("active-user-url");
  safeSessionStorage.removeItem("unsubscribe-user-url");
}
if (credentialPage) {
  const key = credentialPage[1] === "activate" ? "active-user-url" : "unsubscribe-user-url";
  safeSessionStorage.setItem(key, credentialLocation);
}

// "Keep me logged in" off ends with the browser session (PANEL-1633). After the keys above are
// written, which the policy keeps, and before any route reads isLoggedIn().
enforceSessionPolicy();

// A public route's page view, reported each time the route renders, as the react-router 5 render
// props this replaces did (PANEL-1549).
const Tracked = ({ children }) => {
  reportPageview(window.location.pathname + window.location.search);
  return children;
};

// The legacy /finished/:st URL: 10 and fin were completed surveys.
const LegacyFinished = () => {
  const { st } = useParams();
  const completed = st === "10" || st === "fin" ? "/completed" : "";
  return <Navigate to={`/${Panel_language}/finished${completed}`} replace />;
};

const pageLoader = <div className="pageLoaderUpdated"><img src={LoaderSVG} alt="Loading.."/></div>;

export default withRouter(function App({ location }) {
  const navigationPending = useNavigationPending();

  // Read once. The same expression was already spelled three times here before this change and the
  // language-less check would have made it four; it is also the value being stored, so a reader has
  // to be able to see that the thing checked and the thing written are the same thing.
  // NOTE: this is `window.location`, not the router's location. The two differ
  // under a MemoryRouter, and the three pinned url_languge tests drive jsdom history — switching the
  // read is a behaviour change with its own collateral, tracked as W3A-T11-3, not a cleanup.
  const firstSegment = window.location.pathname.split("/")[1]

  // Only a language code: Login offers this cookie as the member's language, and a crafted link could
  // otherwise plant any segment in it (`/en;domain=x/home`).
  if(firstSegment !== "login" && firstSegment !== "" && LANGUAGE_LESS_SEGMENTS.indexOf(firstSegment) === -1 && LANGUAGE.test(firstSegment)){
    setCookie('url_languge',firstSegment,365)
  }

  const hasSession = isLoggedIn();
  // RestrictedRoute can require Login for an unresolved profile without discarding its token.
  const needsLogin = location.state?.reauthenticate === true;
  const skipLogin = hasSession && !needsLogin;

  // Side effects after the commit, not in the render body (CO-PWA-STAT-RENDER): the cover image is
  // fetched once after mount (CO-COVER-STARTUP-HOST), and the installed-PWA statistic when a session
  // appears; setPwaStatistics itself sends it once per tab session.
  React.useEffect(() => {
    getLink();
  }, []);
  React.useEffect(() => {
    // tell server if the pwa is installed.
    if (hasSession) setPwaStatistics();
  }, [hasSession]);

  return (
    <React.Fragment>
      {/* The app's only toast container (PANEL-1538). react-toastify 11 keys containers by id, so
          several id-less ones (the pages used to mount their own) overwrite each other and drop
          toasts. These props keep react-toastify 5's look and behaviour: coloured toasts, no
          icons, click to close, mouse drag; 8000 ms unless a call passes its own autoClose. */}
      <ToastContainer autoClose={8000} theme="colored" icon={false} closeOnClick draggable />
      <React.Suspense fallback={pageLoader}>
        {/* Public callbacks accept only their explicit parameter sets (PANEL-1602). */}
        <Routes>
          <Route path="/:id/finished/:status?" element={<Tracked><Finished/></Tracked>} />
          <Route path="/:id/finished/:status/:project/:panelist/:guid" element={<Tracked><Finished/></Tracked>} />
          <Route path="/:id/activate" element={<Tracked>{isLoggedIn() ? <Activation/> : <Login/>}</Tracked>} />
          <Route path="/:id/activate/:host/:rmsid/:code" element={<Tracked>{isLoggedIn() ? <Activation/> : <Login/>}</Tracked>} />
          <Route path="/:id/unsubscribe" element={<Tracked>{isLoggedIn() ? <ProfileUnsubscribe/> : <Login/>}</Tracked>} />
          <Route path="/:id/unsubscribe/:host/:rmsid/:code" element={<Tracked>{isLoggedIn() ? <ProfileUnsubscribe/> : <Login/>}</Tracked>} />
          <Route path="/:id/login" element={
            <Tracked>{skipLogin ? <Navigate to={`/${Panel_language}/home`} replace /> : <Login/>}</Tracked>
          } />
          <Route path="/:id/home/privacy-policy" element={<Navigate to={`/${Panel_language}/privacy-policy`} replace />} />
          <Route path="/:id/home/terms-and-conditions" element={<Navigate to={`/${Panel_language}/terms-and-conditions`} replace />} />

          <Route path="/:id/home" element={<PrivateRoute name="home" component={HomeGuarded} />} />
          <Route path="/:id/affiliate" element={<RestrictedRoute name="affiliate" component={AffiliateGuarded} />} />
          <Route path="/:id/rewards/:thankyou?" element={<PrivateRoute name="Rewards" component={RewardsGuarded} />} />
          <Route path="/:id/history" element={<PrivateRoute name="SurveyHistory" component={SurveyHistoryGuarded} />} />
          <Route path="/:id/referral-program" element={<PrivateRoute name="referral-program" component={ReferralGuarded} />} />
          <Route path="/:id/help" element={<PrivateRoute name="help" component={HelpGuarded} />} />
          <Route path="/:id/profile" element={<PrivateRoute name="profile" component={ProfileGuarded} />} />
          <Route path="/:id/privacy-policy" element={<PrivateRoute name="privacy-policy" component={PolicyGuarded} />} />
          <Route path="/:id/terms-and-conditions" element={<PrivateRoute name="terms-and-conditions" component={TermnConditionGuarded} />} />
          <Route path="/:id/gdpr-sar" element={<PrivateRoute name="gdpr-ifo" component={GdprInfoGuarded} />} />

          <Route path="/" element={<Navigate to={`/${Panel_language}/${skipLogin ? 'home' : 'login'}`} replace state={needsLogin ? location.state : undefined} />} />
          <Route path="/login" element={<Navigate to={`/${Panel_language}/${skipLogin ? 'home' : 'login'}`} replace state={needsLogin ? location.state : undefined} />} />
          <Route path="/finished/:st?" element={<LegacyFinished/>} />
          <Route path="/finished/:st/:project/:panelist/:guid" element={<LegacyFinished/>} />
          <Route path="/activate" element={<Navigate to={`/${Panel_language}/activate`} replace />} />
          <Route path="/activate/:host/:rmsid/:code" element={<Tracked>{isLoggedIn() ? <Activation/> : <Login/>}</Tracked>} />
          <Route path="/unsubscribe" element={<Navigate to={`/${Panel_language}/unsubscribe`} replace />} />
          <Route path="/unsubscribe/:host/:rmsid/:code" element={<Tracked>{isLoggedIn() ? <ProfileUnsubscribe/> : <Login/>}</Tracked>} />
          <Route path="*" element={<NotFound/>} />
        </Routes>
      </React.Suspense>
      {/* While a navigation waits for a page's code, the current screen stays (AppRouter, PANEL-1539);
          the loader goes over it, where the Suspense fallback used to replace it. */}
      {navigationPending && pageLoader}
    </React.Fragment>
  );
})
